import { createSelector } from "@reduxjs/toolkit";
import {
  ExperienceBadge,
  ExperiencesAgeBandEnum,
  ExperiencesCancellationSummary,
  ExperiencesPriceBreakdown,
  ITrackingProperties,
  Prices,
  ParsedExperienceBookableItem,
  CallState,
  IExperiencesAgeBandCounts,
  ActivitiesDetailsProperties,
} from "redmond";
import queryStringParser from "query-string";

import { IStoreState } from "../../../../reducers/types";
import { IShopOverview } from "../../components/ShopOverview/component";
import { IShopWhatsIncludedProps } from "../../components/ShopWhatsIncluded/component";
import { IShopWhatToExpect } from "../../components/ShopWhatToExpect/component";
import { IShopPhotoGalleryProps } from "../../components/ShopPhotoGallery/component";
import { IShopSimilar } from "../../components/ShopSimilar/component";
import { IShopCancellation } from "../../components/ShopCancellationPolicy/component";
import { IShopCustomerReviews } from "../../components/ShopCustomerReviews/component";
import { IShopContactInformationProps } from "../../../common";
import { IShopCheckoutBreakdown } from "../../components/ShopCheckoutBreakdown/component";
import { getActivitiesAvailabilityProperties } from "../../../availability/reducer";

export const getExperiencesShopCallState = (state: IStoreState) =>
  state.experiencesShop.experiencesShopCallState;

export const getExperiencesShopOptionsCallState = (state: IStoreState) =>
  state.experiencesShop.experiencesShopOptionsCallState;

export const getSelectedExperienceId = (state: IStoreState) =>
  state.experiencesShop.selectedExperienceId;

export const getExperiencesShopDetails = (state: IStoreState) =>
  state.experiencesShop.experiencesShopDetails;

export const getBestTravelOffer = (state: IStoreState) =>
  state.experiencesShop.bestTravelOffer;

export const getTravelerCounts = (state: IStoreState) =>
  state.experiencesShop.travelerCounts;

export const getExperiencesShopReviews = (state: IStoreState) =>
  state.experiencesShop.selectedExperienceReviews;

export const getExperiencesShopPriceBreakdowns = (state: IStoreState) =>
  state.experiencesShop.experiencesPriceBreakdowns;

export const getExperiencesNoAvailability = (state: IStoreState) =>
  state.experiencesShop.experiencesPriceBreakdowns?.length === 0;

export const getSelectedTime = (state: IStoreState) =>
  state.experiencesShop.selectedTime;

export const getSelectedDate = (state: IStoreState) =>
  state.experiencesShop.selectedDate;

export const getSelectedBookableItem = (state: IStoreState) =>
  state.experiencesShop.selectedBookableItem;

export const getNextAvailableDate = (state: IStoreState) =>
  state.experiencesShop.nextAvailableDate;

export const getNextAvailableDateCallState = (state: IStoreState) =>
  state.experiencesShop.nextAvailableDateCallState;

export const getShopOptionTrackingProperties = (state: IStoreState) =>
  state.experiencesShop.shopOptionTrackingProperties;

export const getActivityIndex = (state: IStoreState) =>
  state.experiencesShop.activityIndex;

export const getExperienceId = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return null;

    return experiencesShopDetails.id;
  },
);

export interface ParsedAgeBand {
  priceByCount?: (count: number) => Prices | undefined;
  minNumberOfTravelers?: number;
  maxNumberOfTravelers?: number;
  minAge?: number;
  maxAge?: number;
  priceVariesByCount?: boolean;
}

export const getExperiencesShopAgeBands = createSelector(
  getExperiencesShopDetails,
  getSelectedDate,
  getSelectedBookableItem,
  (experiencesShopDetails, selectedDate, selectedBookableItem) => {
    if (!experiencesShopDetails || !selectedDate || !selectedBookableItem)
      return undefined;
    const parsedAgeBands: {
      [key in ExperiencesAgeBandEnum]?: ParsedAgeBand;
    } = {};
    const ageBandsForDate =
      selectedBookableItem.ageBandPricesByDate[selectedDate];
    if (!ageBandsForDate) {
      return undefined;
    }
    Object.values(ExperiencesAgeBandEnum).forEach((value) => {
      const matchingAgeBandsForDate = ageBandsForDate.filter(
        (ageBand) => ageBand.ageBand === value,
      );
      const minTravelersBand =
        matchingAgeBandsForDate.length > 0
          ? matchingAgeBandsForDate.reduce((prev, current) =>
              (prev.minNumberOfTravelers ?? Infinity) <
              (current.minNumberOfTravelers ?? Infinity)
                ? prev
                : current,
            )
          : null;
      const maxTravelersBand =
        matchingAgeBandsForDate.length > 0
          ? matchingAgeBandsForDate.reduce((prev, current) =>
              (prev.maxNumberOfTravelers ?? -Infinity) >
              (current.maxNumberOfTravelers ?? -Infinity)
                ? prev
                : current,
            )
          : null;

      const priceByCount = (count: number) => {
        const matchingBand = matchingAgeBandsForDate.find(
          (ageBand) =>
            (ageBand.minNumberOfTravelers ?? -Infinity) <= count &&
            (ageBand.maxNumberOfTravelers ?? Infinity) >= count,
        );
        return matchingBand?.pricePerTraveler;
      };

      if (minTravelersBand && maxTravelersBand) {
        parsedAgeBands[value] = {
          priceByCount,
          minNumberOfTravelers: minTravelersBand?.minNumberOfTravelers,
          maxNumberOfTravelers: maxTravelersBand?.maxNumberOfTravelers,
          minAge: minTravelersBand?.lowerBound,
          maxAge: minTravelersBand?.upperBound,
          priceVariesByCount: matchingAgeBandsForDate.length > 1,
        };
      }
    });
    return parsedAgeBands;
  },
);

export const getTravelerCountsWithMinimums = createSelector(
  getTravelerCounts,
  getExperiencesShopAgeBands,
  (travelerCounts, parsedAgeBands) => {
    let counts = travelerCounts;
    if (!parsedAgeBands) return undefined;

    if (!counts) {
      if (parsedAgeBands.Adult) {
        counts = {
          adultsCount: 1,
          seniorsCount: 0,
          youthsCount: 0,
          childrenCount: 0,
          infantsCount: 0,
        };
      } else {
        counts = {
          travelerCount: 1,
        };
      }
    }

    if (parsedAgeBands.Adult) {
      const ageBandCounts = counts as IExperiencesAgeBandCounts;
      let adultsCount;

      // if no adults is selected, but more than one seniors are, let adultsCount continue to be 0
      // otherwise it would've been overridden to 1 by (parsedAgeBands?.Adult?.minNumberOfTravelers || 1)
      if (
        (ageBandCounts?.adultsCount || 0) === 0 &&
        (ageBandCounts?.seniorsCount || 0) > 0
      ) {
        adultsCount = 0;
      } else {
        adultsCount =
          ageBandCounts.adultsCount <
          (parsedAgeBands?.Adult?.minNumberOfTravelers || 1)
            ? parsedAgeBands?.Adult?.minNumberOfTravelers || 1
            : ageBandCounts.adultsCount;
      }

      return {
        ...ageBandCounts,
        adultsCount,
        seniorsCount:
          (ageBandCounts?.seniorsCount || 0) <
          (parsedAgeBands?.Senior?.minNumberOfTravelers || 0)
            ? parsedAgeBands?.Senior?.minNumberOfTravelers || 0
            : ageBandCounts.seniorsCount,
        youthsCount:
          (ageBandCounts?.youthsCount || 0) <
          (parsedAgeBands?.Youth?.minNumberOfTravelers || 0)
            ? parsedAgeBands?.Youth?.minNumberOfTravelers || 0
            : ageBandCounts.youthsCount,
        childrenCount:
          ageBandCounts.childrenCount <
          (parsedAgeBands?.Child?.minNumberOfTravelers || 0)
            ? parsedAgeBands?.Child?.minNumberOfTravelers || 0
            : ageBandCounts.childrenCount,
        infantsCount:
          (ageBandCounts?.infantsCount || 0) <
          (parsedAgeBands?.Infant?.minNumberOfTravelers || 0)
            ? parsedAgeBands?.Infant?.minNumberOfTravelers || 0
            : ageBandCounts.infantsCount,
      };
    } 
      return {
        ...counts,
      };
    
  },
);

export const getExperienceName = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return null;

    return experiencesShopDetails.name;
  },
);

export const getExperienceProviderName = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails) => {
    if (!experiencesShopDetails) return null;

    return experiencesShopDetails.supplierInfo.name;
  },
);

export const getExperiencesSearchLocation = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): string | null => {
    const { locationName } = queryStringParser.parse(window.location.search);
    const urlLocationName = Array.isArray(locationName)
      ? locationName[0]
      : locationName;

    if (urlLocationName) return urlLocationName;

    return experienceShopDetails?.searchLocation ?? null;
  },
);

export const getExperiencesShopBookableItems = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): ParsedExperienceBookableItem[] => {
    if (!experienceShopDetails) return [];

    const filteredBookableItems: ParsedExperienceBookableItem[] = [];
    experienceShopDetails.bookableItems.forEach((bookableItem) => {
      if (Object.entries(bookableItem.timeSlotsByDate).length > 0) {
        const firstAvailableDate = Object.keys(
          bookableItem.timeSlotsByDate,
        ).find((date) =>
          bookableItem.timeSlotsByDate[date].find((y) => y.price),
        );
        const firstAvailablePrice = firstAvailableDate
          ? bookableItem.timeSlotsByDate[firstAvailableDate].find(
              (y) => y.price,
            )
          : undefined;

        if (firstAvailableDate && firstAvailablePrice) {
          filteredBookableItems.push({
            ...bookableItem,
            firstAvailablePrice,
          });
        }
      }
    });

    filteredBookableItems.sort(
      (a: ParsedExperienceBookableItem, b: ParsedExperienceBookableItem) => {
        if (a.firstAvailablePrice.price && b.firstAvailablePrice.price) {
          return (
            a.firstAvailablePrice.price.fiat.value -
            b.firstAvailablePrice.price.fiat.value
          );
        }
        return 0;
      },
    );

    return filteredBookableItems;
  },
);

export const getExperienceMaxTravelerCount = createSelector(
  getExperiencesShopDetails,
  (experiencesShopDetails): number | undefined => {
    if (!experiencesShopDetails) return undefined;

    return experiencesShopDetails.bookingRequirements.maxTravellers;
  },
);

export const getExperiencesShopOverview = createSelector(
  getExperiencesShopDetails,
  getExperiencesShopBookableItems,
  getExperienceMaxTravelerCount,
  getExperiencesShopPriceBreakdowns,
  (
    experienceShopDetails,
    bookableItems,
    maxTravelerCount,
    priceBreakdowns,
  ): IShopOverview | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      title: experienceShopDetails.name,
      experienceId: experienceShopDetails.id,
      overview: experienceShopDetails.overview,
      rating: experienceShopDetails.rating,
      duration: experienceShopDetails.duration,
      durationFromMinutes: experienceShopDetails.durationFromMinutes,
      durationToMinutes: experienceShopDetails.durationToMinutes,
      locationLogistics: experienceShopDetails.logistics,
      languageGuides: experienceShopDetails.languageGuides || [],
      provider: experienceShopDetails.supplierInfo.name,
      badges: experienceShopDetails.badges.filter(
        (badge) => badge !== ExperienceBadge.FreeCancellation,
      ),
      bookableItems,
      pricingCategory: experienceShopDetails.pricingCategory,
      experienceMaxTravelers: maxTravelerCount,
      adultRequired: experienceShopDetails.bookingRequirements.adultRequired,
      dailyPrices: experienceShopDetails.dailyPrices,
      ticket:
        experienceShopDetails.ticketInfo?.ticketTypes &&
        experienceShopDetails.ticketInfo?.ticketTypes.length > 0
          ? experienceShopDetails.ticketInfo?.ticketTypes[0]
          : undefined,
      timeSelectOptions:
        priceBreakdowns?.map((breakdown) => breakdown.startTime || "") || [],
      pricingOpaque: experienceShopDetails.pricingOpaque,
      startingTimeZone: experienceShopDetails.startingTimeZone,
    };
  },
);

export const getExperiencesShopWhatsIncluded = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopWhatsIncludedProps | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      additionalInformation: experienceShopDetails.additionalInformation,
      knowBeforeYouBook: experienceShopDetails.knowBeforeYouBook,
      inclusionsAndExclusions: experienceShopDetails.inclusionsAndExclusions,
    };
  },
);

export const getExperiencesShopCancellationPolicy = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopCancellation | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      cancellationPolicy: experienceShopDetails.cancellationPolicy,
      startingTimeZone: experienceShopDetails.startingTimeZone,
    };
  },
);

export const getExperiencesShopContactInfo = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopContactInformationProps | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      supplierEmail: experienceShopDetails.supplierInfo.email,
      supplierPhone: experienceShopDetails.supplierInfo.phone,
    };
  },
);

export const getExperiencesShopWhatToExpect = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopWhatToExpect | null => {
    if (!experienceShopDetails) return null;

    return {
      isSkeleton: false,
      itinerary: experienceShopDetails.itinerary,
    };
  },
);

export const getExperiencesShopCustomerReviews = createSelector(
  getExperiencesShopReviews,
  getExperiencesShopDetails,
  (customerReviews, experienceShopDetails): IShopCustomerReviews | null => {
    if (!customerReviews && !experienceShopDetails) return null;

    return {
      isSkeleton: false,
      summary: customerReviews?.summary ?? experienceShopDetails?.rating,
      reviews: customerReviews?.reviews ?? [],
    };
  },
);

// export const getExperiencesShopBookableItemsToDisplay = createSelector(
//   getExperiencesShopDetails,
//   (
//     experiencesShopDetails
//   ): {
//     experienceDailyPrice: ExperienceDailyPrice;
//     startTimes: string[];
//   } | null => {
//     if (!experiencesShopDetails) return null;

//     const bookableItems = experiencesShopDetails.bookableItems;
//     const dailyPrices = experiencesShopDetails.dailyPrices;

//     const parsedBookableItems = [];
//     dailyPrices.slice(0, 4).map((dailyPrice) => {
//       bookableItems.map(() => {

//       })
//     })
//     return null;
//   }
// );

export const getExperiencesShopGallery = createSelector(
  getExperiencesShopDetails,
  getExperiencesShopReviews,
  getExperienceMaxTravelerCount,
  (
    experienceShopDetails,
    customerReviews,
    maxTravelerCount,
  ): IShopPhotoGalleryProps | null => {
    if (!experienceShopDetails) return null;

    const {
      coverImage,
      additionalImages,
      name,
      duration,
      rating,
      badges,
      pricingCategory,
    } = experienceShopDetails;
    const providerPhotos = [coverImage, ...additionalImages];

    const reviewsWithPhotos = (customerReviews?.reviews || []).filter(
      ({ travelerPhotos }) => !!travelerPhotos.length,
    );
    const travelerPhotos = reviewsWithPhotos.map(
      (review) => review.travelerPhotos[0],
    );

    return {
      isSkeleton: false,
      providerPhotos,
      travelerPhotos,
      experienceName: name,
      duration,
      rating,
      isFreeCancellation: badges.includes(ExperienceBadge.FreeCancellation),
      experienceMaxTravelers: maxTravelerCount,
      pricingCategory,
    };
  },
);

export const getExperiencesShopSimilar = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): IShopSimilar | null => {
    if (!experienceShopDetails) return null;

    const {
      coverImage,
      additionalImages,
      name,
      overview,
      duration,
      rating,
      badges,
      similarExperiences,
      pricingCategory,
      bestPrice,
      id,
      languageGuides,
      itinerary,
      pricingOpaque,
    } = experienceShopDetails;

    return {
      isSkeleton: false,
      selectedExperienceInfo: {
        name,
        rating,
        duration,
        coverImage,
        additionalImages,
        cancellationSummary: badges.includes(ExperienceBadge.FreeCancellation)
          ? ExperiencesCancellationSummary.FreeCancellation
          : ExperiencesCancellationSummary.PaidCancellation,
        pricingCategory,
        bestPrice,
        id,
        languageGuides,
        itinerary,
        description: overview,
        tags: [],
        popularFeatureTags: [],
        badges,
        pricingOpaque,
        mayHaveExtraChargesDueUponArrival: false,
      },
      similarExperiences,
    };
  },
);

export const getExperiencesShopPriceBreakdown = createSelector(
  getExperiencesShopPriceBreakdowns,
  getExperiencesShopDetails,
  getSelectedTime,
  (
    priceBreakdowns,
    experiencesShopDetails,
    selectedTime,
  ): ExperiencesPriceBreakdown | undefined => {
    if (
      !experiencesShopDetails ||
      !priceBreakdowns ||
      priceBreakdowns.length === 0
    )
      return undefined;

    if (selectedTime) {
      return (
        priceBreakdowns.find(
          (breakdown) => breakdown.startTime === selectedTime,
        ) || priceBreakdowns[0]
      );
    }

    return priceBreakdowns[0];
  },
);

export const getIsFreeCancellation = createSelector(
  getExperiencesShopDetails,
  (experienceShopDetails): boolean => {
    if (!experienceShopDetails) return false;

    return (
      experienceShopDetails.badges.length > 0 &&
      experienceShopDetails.badges.includes(ExperienceBadge.FreeCancellation)
    );
  },
);

export const getExperiencesCheckoutBreakdown = createSelector(
  getExperiencesShopPriceBreakdown,
  getExperiencesShopOptionsCallState,
  getExperiencesShopCallState,
  getExperienceMaxTravelerCount,
  getIsFreeCancellation,
  getExperiencesShopDetails,
  getBestTravelOffer,
  (
    priceBreakdown,
    shopOptionCallState,
    shopCallState,
    maxTravelerCount,
    isFreeCancellation,
    experiencesShopDetails,
    bestTravelOffer,
  ): IShopCheckoutBreakdown | null => ({
    isLoading:
      shopCallState === CallState.NotCalled ||
      shopCallState === CallState.InProcess ||
      shopOptionCallState === CallState.NotCalled ||
      shopOptionCallState === CallState.InProcess,
    priceBreakdown,
    shopOptionCallState,
    experienceMaxTravelers: maxTravelerCount,
    isFreeCancellation,
    pricingCategory: experiencesShopDetails?.pricingCategory,
    bestTravelOffer,
  }),
);

export const getActivityDetailsProperties = createSelector(
  getExperiencesShopDetails,
  getShopOptionTrackingProperties,
  getActivitiesAvailabilityProperties,
  getActivityIndex,
  getBestTravelOffer,
  getExperiencesSearchLocation,
  (
    shopDetails,
    shopOptionTracking,
    activitiesAvailabilityProperties,
    activityIndex,
    bestTravelOffer,
    experiencesSearchLocation,
  ): ITrackingProperties<ActivitiesDetailsProperties> => ({
    properties: {
      ...activitiesAvailabilityProperties.properties,
      ...shopDetails?.trackingProperties.properties,
      activity_provider: shopDetails?.supplierInfo.name,
      avg_rating: shopDetails?.rating.reviewAverage,
      activity_name: shopDetails?.name,
      number_of_photos: shopDetails?.additionalImages.length,
      number_of_reviews: shopDetails?.rating.numberOfReviews,
      productId: shopDetails?.id.value,
      product_sort_index: activityIndex,
      ...shopDetails?.trackingProperties.properties,
      ...shopOptionTracking?.properties,
      // shopOptionTracking has the following properties
      // ag_1, ag_2, ag_3, ag_4, ag_5, age_groups, markup_percentage, total_price, original_net, rrp
      // Last so the spreads above can't overwrite it.
      location_searched:
        activitiesAvailabilityProperties.properties.location_searched ??
        experiencesSearchLocation ??
        undefined,
    },
    encryptedProperties: [
      ...activitiesAvailabilityProperties.encryptedProperties,
      shopDetails?.trackingProperties.encryptedProperties ?? "",
      shopOptionTracking?.encryptedProperties ?? "",
      bestTravelOffer?.trackingPropertiesV2?.encryptedProperties ?? "",
    ],
  }),
);

export const getOpaqueActivityToken = (state: IStoreState) =>
  state.experiencesShop.opaqueActivityToken;
